<script setup lang="ts">
import { Head, Link, usePage } from "@inertiajs/vue3";
import { computed } from "vue";

import {
  organizationDirectoryPath,
  organizationDiscussionGroupsPath,
  organizationDiscussionThreadsPath,
  settingsProfilePath,
} from "@/routes";
import type { SharedData } from "@/types";

interface HelpSection {
  id: string;
  title: string;
  whatIs: string;
  steps: [string, string, string];
  example: string;
  ctaLabel: string;
  ctaHref: string;
  ctaDisabled: boolean;
}

const page = usePage<SharedData>();
const organizationId = computed(() => page.props.auth.organization?.id);

const sections = computed<HelpSection[]>(() => {
  const orgId = organizationId.value;
  const threadsHref = orgId ? organizationDiscussionThreadsPath(orgId) : "#";
  const directoryHref = orgId ? organizationDirectoryPath(orgId) : "#";
  const groupsHref = orgId ? organizationDiscussionGroupsPath(orgId) : "#";
  const missingOrg = !orgId;

  return [
    {
      id: "hilos",
      title: "Hilos",
      whatIs: "Un hilo es una conversación por tema. Sirve para no mezclar asuntos.",
      steps: [
        "Entrá a Hilos y abrí un tema existente o creá uno nuevo.",
        "Respondé en el hilo y mencioná a personas o grupos con @.",
        "Usalo cuando el tema necesita seguimiento y contexto.",
      ],
      example: "Ejemplo: “Pago proveedor febrero” en un solo hilo con compras y finanzas.",
      ctaLabel: "Ir a Hilos",
      ctaHref: threadsHref,
      ctaDisabled: missingOrg,
    },
    {
      id: "directorio",
      title: "Directorio",
      whatIs: "El directorio te ayuda a encontrar personas y grupos rápido.",
      steps: [
        "Buscá por nombre, cargo, bio o grupo.",
        "Seleccioná personas o grupos desde la lista.",
        "Creá un hilo nuevo con la selección para empezar la conversación.",
      ],
      example: "Ejemplo: buscá “facturación”, seleccioná al equipo y abrí un hilo de consultas.",
      ctaLabel: "Ir a Directorio",
      ctaHref: directoryHref,
      ctaDisabled: missingOrg,
    },
    {
      id: "grupos",
      title: "Crear grupo y asignar usuarios",
      whatIs: "Los grupos ordenan la comunicación por área o función. Solo owner puede gestionarlos.",
      steps: [
        "Entrá a Grupos y creá un grupo con un nombre claro.",
        "Agregá una descripción corta del uso del grupo.",
        "Asigná usuarios al grupo para poder mencionarlo en hilos.",
      ],
      example: "Ejemplo: grupo “taller” para mantenimiento y solicitudes técnicas.",
      ctaLabel: "Ir a Grupos",
      ctaHref: groupsHref,
      ctaDisabled: missingOrg,
    },
    {
      id: "desasignarme",
      title: "Desasignarme de un hilo",
      whatIs: "Desasignarte te saca de participantes y deja de enviarte actividad del hilo.",
      steps: [
        "Abrí el hilo y entrá a Detalles.",
        "En Participantes, buscá tu usuario.",
        "Elegí quitarme para dejar de seguir ese hilo.",
      ],
      example: "Ejemplo: si ya cerraste tu parte del tema, te quitás y evitás ruido.",
      ctaLabel: "Ir a Hilos",
      ctaHref: threadsHref,
      ctaDisabled: missingOrg,
    },
    {
      id: "archivar",
      title: "Archivar un hilo",
      whatIs: "Archivar oculta el hilo activo para todo el equipo, sin borrar historial.",
      steps: [
        "Abrí el hilo y entrá a Detalles.",
        "Activá la opción de archivar.",
        "Para desarchivarlo, cualquier participante puede apagar el switch en Detalles.",
      ],
      example: "Ejemplo: archivá un tema resuelto; vuelve a activo cuando alguien lo desarchive manualmente.",
      ctaLabel: "Ir a Hilos",
      ctaHref: threadsHref,
      ctaDisabled: missingOrg,
    },
    {
      id: "perfil",
      title: "Mini perfil",
      whatIs: "Tu mini perfil mejora cómo te encuentran en el directorio.",
      steps: [
        "Entrá a Ajustes > Perfil.",
        "Completá cargo y mini bio con palabras simples.",
        "Guardá para aparecer mejor en búsquedas internas.",
      ],
      example: "Ejemplo: cargo “Analista de compras”, bio “Licitaciones y proveedores críticos”.",
      ctaLabel: "Ir a Perfil",
      ctaHref: settingsProfilePath(),
      ctaDisabled: false,
    },
  ];
});
</script>

<template>
  <Head title="Ayuda" />

  <div class="relative flex h-full min-h-0 w-full flex-col gap-3 overflow-y-auto bg-background p-3">
    <section class="rounded-2xl border border-border/70 bg-card/70 p-5">
      <h1 class="text-xl font-semibold tracking-tight sm:text-2xl">Guía rápida de uso</h1>
      <p class="mt-1.5 max-w-3xl text-sm text-muted-foreground">
        Lo esencial para trabajar en Condor sin pasos técnicos ni vueltas largas.
      </p>
    </section>

    <section class="grid gap-3 pb-4 lg:grid-cols-2">
      <article
        v-for="section in sections"
        :key="section.id"
        class="rounded-2xl border border-border/70 bg-background p-5"
      >
        <h2 class="text-base font-semibold tracking-tight">{{ section.title }}</h2>

        <div class="mt-3 space-y-3 text-sm">
          <div>
            <p class="font-medium text-foreground">Qué es</p>
            <p class="mt-1 text-muted-foreground">{{ section.whatIs }}</p>
          </div>

          <div>
            <p class="font-medium text-foreground">Cómo hacerlo (3 pasos)</p>
            <ol class="mt-1 space-y-1 text-muted-foreground">
              <li v-for="(step, index) in section.steps" :key="`${section.id}-step-${index}`">
                {{ index + 1 }}. {{ step }}
              </li>
            </ol>
          </div>

          <div>
            <p class="font-medium text-foreground">Ejemplo</p>
            <p class="mt-1 text-muted-foreground">{{ section.example }}</p>
          </div>
        </div>

        <div class="mt-4">
          <Link
            v-if="!section.ctaDisabled"
            :href="section.ctaHref"
            class="inline-flex items-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
          >
            {{ section.ctaLabel }}
          </Link>
          <span
            v-else
            class="inline-flex items-center rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground"
          >
            Organización no disponible
          </span>
        </div>
      </article>
    </section>
  </div>
</template>
